import React, { useMemo } from "react";
import LayerForm from "modules/profileIDE/connectors/form";
import EditorLayout from "modules/profileIDE/components/Editor/layout";
import RadioGroup from "components/ui/RadioGroup";
import { createConnector } from "modules/binder";
import { connectField, ReadOnlyProvider } from "modules/form";
import Forms from "modules/profileIDE/components/Forms";
import Switch from "components/common/Switch";
import Editor, { Toolbar } from "components/common/Editor";
import Icon from "components/ui/Icon";
import styled, { css } from "styled-components";
import { faCode } from "@fortawesome/pro-regular-svg-icons";
import FormInputIcon from "assets/icons/form_input.svg?component";
import DeleteFileButton from "./DeleteFileButton";
import { createSelector } from "reselect";
import { appProfileMacrosFetcher } from "modules/profileIDE/state/editor";
import store from "services/store";
import { PermissionService } from "services/permissions";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";

const Group = styled.div`
  > div {
    display: flex;
    flex-direction: row;
    overflow: hidden;
    border-radius: 3px;
    margin: 0px 8px;
  }
`;
const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  cursor: pointer;

  svg {
    font-size: 17px;
  }

  ${(props) =>
    props.selected &&
    css`
      background: #818ea1;
      color: #fff;
    `}
`;

const HeaderContent = styled.div`
  margin: 0 20px;
`;

const EditorToggle = createConnector({
  selectors: {
    value: (state) => state.editor.mode,
    fileType: (state) => {
      const activeFile = state.fileList.activeFile;
      return state.fileList.entities?.[activeFile]?.type || "";
    },
  },
  actions: {
    onChange: (value) => (dispatch, module) => {
      dispatch({
        type: module.actions.editor.switchMode,
        mode: value,
      });
    },
  },
}).connect(({ value, fileType, onChange }) => {
  const { t } = useTranslation();

  const TABS = useMemo(
    () => [
      {
        key: "form",
        value: "form",
        icon: { component: FormInputIcon },
        hidden: fileType === "child-manifest",
        description: t("Form mode"),
      },
      {
        key: "editor",
        value: "editor",
        icon: { awesome: faCode },
        hidden: ["operator-instance", "manifest", "container"].includes(
          fileType
        ),
        description: t("Editor mode"),
      },
    ],
    [fileType, t]
  );
  function renderTab(tab) {
    const { value, icon, key, disabled, hidden, description } = tab;
    if (hidden) {
      return null;
    }

    return (
      <IconWrap disabled={disabled} key={key} value={value}>
        <Tooltip title={description} placement="bottom">
          <Icon {...icon} />
        </Tooltip>
      </IconWrap>
    );
  }
  return (
    <Group>
      <RadioGroup data-qa="editor-tabs" value={value} onChange={onChange}>
        {TABS.map(renderTab)}
      </RadioGroup>
    </Group>
  );
});

const ConnectedEditor = connectField(Editor);
const EditorWrap = styled.div`
  width: calc(100% + 48px);
  height: calc(100% + 48px);
  margin: -24px;
  display: flex;
  flex-direction: row;

  ${Toolbar} {
    display: none;
  }
`;

// This WILL need to be moved to exception due to the fact that it would be able to be used for cluster profiles also
const EditorContent = createConnector({
  selectors: {
    mode: (state) => state.editor.mode,
    macros: createSelector(
      () => appProfileMacrosFetcher.selector(store.getState())?.result,
      (state) => state.drafts,
      (state) => state.fileList.files,
      (state) => state.fileList.childrenMap,
      (state) => state.fileList.activeFile,
      (macros, drafts, files, childrenMap, activeFile) => {
        const filteredMacros = (macros || []).filter(
          (macro) => !macro.includes("#")
        );

        const outputParameterDependentMacros = (macros || []).filter((macro) =>
          macro.includes("#tierOutputParam")
        );
        const inputParameterDependentMacros = (macros || []).filter((macro) =>
          macro.includes("#tierInputParam")
        );

        const operatorTiersDraftsGuids = (Object?.keys(drafts) || []).filter(
          (draftGuid) =>
            ["operator-instance", "container"].includes(
              drafts?.[draftGuid]?.type
            )
        );

        const operatorTiersMacros = operatorTiersDraftsGuids.flatMap(
          (operatorDraftGuid) => {
            let activeFileIndex = files.indexOf(activeFile);

            // child file parent index
            if (activeFileIndex === -1) {
              const parentGuid = Object.keys(childrenMap).find((parentGuid) =>
                childrenMap[parentGuid].includes(activeFile)
              );

              activeFileIndex = files.indexOf(parentGuid);
            }

            if (files.indexOf(operatorDraftGuid) < activeFileIndex) {
              return [];
            }

            const operatorDraftData = drafts[operatorDraftGuid];
            const outputParametersMacros = (
              operatorDraftData?.parameters?.outputParameters || []
            ).flatMap((outputParameter) => {
              return outputParameterDependentMacros.map((outputMacro) =>
                outputMacro
                  .replace("#tierName", operatorDraftData.name)
                  .replace("#tierOutputParam", outputParameter.name)
              );
            });

            return outputParametersMacros;
          }
        );

        const currentDraftData = drafts[activeFile];
        const currentTierInputMacros = (
          currentDraftData?.parameters?.inputParameters || []
        ).flatMap((inputParameter) => {
          return inputParameterDependentMacros.map((inputMacro) =>
            inputMacro
              .replace("#tierName", currentDraftData.name)
              .replace("#tierInputParam", inputParameter.name)
          );
        });

        return [
          ...filteredMacros,
          ...currentTierInputMacros,
          ...operatorTiersMacros,
        ];
      }
    ),
    readOnly: (state) => {
      const isEditMode = state.editor?.isEdit;
      const { permissions } = state?.fileList?.metadata?.annotations || {};
      const appProfilePermissions = new PermissionService(
        permissions?.split(",")
      );
      return !appProfilePermissions.has("appProfile.update") && isEditMode;
    },
  },
}).connect(({ mode, macros, readOnly }) => {
  return (
    <ReadOnlyProvider isActive={readOnly}>
      <Switch value={mode}>
        <Switch.Case value="form">
          <Forms />
        </Switch.Case>
        <Switch.Case value="editor">
          <EditorWrap>
            <ConnectedEditor
              name="values"
              macros={macros}
              readOnly={readOnly}
            />
          </EditorWrap>
        </Switch.Case>
      </Switch>
    </ReadOnlyProvider>
  );
});

const EditorHeader = createConnector({
  selectors: {
    fileName: (state) => {
      const activeFile = state.fileList.activeFile;
      return state.fileList.entities?.[activeFile]?.name || "";
    },
  },
}).connect(({ fileName }) => <HeaderContent>{fileName}</HeaderContent>);

export default function AppProfileEditor() {
  return (
    <>
      <EditorLayout />
      <EditorLayout.HeaderName>
        <EditorHeader />
      </EditorLayout.HeaderName>
      <EditorLayout.DeleteFile>
        <DeleteFileButton />
      </EditorLayout.DeleteFile>
      <EditorLayout.HeaderTabs>
        <EditorToggle />
      </EditorLayout.HeaderTabs>
      <EditorLayout.Content>
        <LayerForm>
          <EditorContent />
        </LayerForm>
      </EditorLayout.Content>
    </>
  );
}
